import React from 'react';

// Styles & Assets
import styles from './TakenDialog.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';
import { getTakenValue, getNonTakenValue } from '../../../services/Pharmacy';

// Hooks
import { useState, useRef } from 'react';
import useHttp from '../../../hooks/useHttp';
import { useTranslation } from 'react-i18next';

// Components
import TakenOptions from '../TakenOptions/TakenOptions';
import NonTakenOptions from '../NonTakenOptions/NonTakenOptions';

// Interfaces
import { DrugAdministration, DrugAdministrationBody } from '../../../interfaces/Drug';

const TakenDialog: React.FC<{
    drugName: string,
    administrations: DrugAdministration[],
    time: { date: string, hour: string },
    administrationID: number | undefined
    showDialog: React.Dispatch<React.SetStateAction<boolean>>
    handleConfirmTaken: (body: DrugAdministrationBody) => void
}> = ({ drugName, administrations, time, administrationID, showDialog, handleConfirmTaken }) => {

    const { t } = useTranslation();

    const drugAdministred = administrations[0];

    const { sendRequest } = useHttp();

    const [taken, setTaken] = useState<boolean | null>(getTakenValue(drugAdministred) ?? true);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [professionalAdmin, setProfessionalAdmin] = useState<boolean>(false);
    const [counter, setCounter] = useState<number>(drugAdministred.rescue_dose_number ?? 0);

    const [forgotten, setForgotten] = useState<boolean>(getNonTakenValue(drugAdministred));
    const defaultComment = drugAdministred.cancellation_reason ?? ''
    const commentRef = useRef<string>(defaultComment);


    const handleConfirm = () => {
        if (taken === false && !forgotten && commentRef.current.trim() === '') {
            setErrorMsg(t('reason_must_be_specified'));
            return;
        }
        const body: DrugAdministrationBody = {
            rescue_dose: counter === 0 ? false : true,
            rescue_dose_number: counter,
            status: taken ?
                'administred' : forgotten ?
                    'forgotten' : 'cancelled',
            cancellation_reason: forgotten ?
                null : commentRef.current
        };
        sendRequest({
            url: getEnvironmentApiUrl() + 'drug_administration/' + administrationID,
            method: 'PUT',
            body
        }, () => handleConfirmTaken(body));
        showDialog(false);
    };

    const handleChangeAdmin = () =>
        setProfessionalAdmin(value => !value);

    const handleChangeCounter = (increment: number) =>
        setCounter(oldCounter => oldCounter + increment);

    const handleSetForgotten = (forgot: boolean) => setForgotten(forgot);

    const handleChangeComment = (comment: string) => commentRef.current = comment;

    return (
        <div className='dialog'>
            <h1 className='dialogTitle'>{drugName}</h1>
            <h2 className='dialogSubTitle'>
                {time.date.replaceAll('-', '/') + ' - ' + time.hour}
            </h2>
            <div className={styles.takenButtons}>
                <button
                    onClick={() => setTaken(true)}
                    className={`button ${styles.takenButton} ${taken === true ? styles.selectedButton : ''}`}
                >
                    {t('taken')}
                </button>
                <button
                    onClick={() => setTaken(false)}
                    className={`button ${styles.takenButton} ${taken === false ? styles.selectedButton : ''}`}
                >
                    {t('not_taken')}
                </button>
            </div>
            <div className={styles.optionsContainer}>
                {taken ?
                    <TakenOptions
                        professionalAdmin={professionalAdmin}
                        counter={counter}
                        handleChangeAdmin={handleChangeAdmin}
                        handleChangeCounter={handleChangeCounter}
                    />
                    :
                    <NonTakenOptions
                        forgotten={forgotten}
                        handleSetForgotten={handleSetForgotten}
                        defaultComment={defaultComment}
                        handleChangeComment={handleChangeComment}
                    />
                }
            </div>
            <p className={styles.errorMsg}>
                {errorMsg}
            </p>
            <div className={styles.buttonContainer}>
                <button disabled={taken === null} onClick={handleConfirm} className={`button ${styles.confirmButton}`}>
                    {t('confirm')}
                </button>
            </div>
        </div>
    );
};

export default TakenDialog;